import type {
  AnalyticsOverview,
  BillingDto,
  TeamDto,
  UpdateBillingDto,
  ChecklistDto,
  ConnectChallengeDto,
  NotificationPrefsDto,
  Organization,
  PlatformAccountDto,
  ReviewDto,
  ReviewFeed,
  SessionDto,
} from '@rp/contracts';
import { getInitData } from './telegram';

const BASE_URL = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '/api';

let accessToken: string | null = null;
let organizationId: string | null = null;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};
export const setOrganizationId = (id: string | null): void => {
  organizationId = id;
};
export const getOrganizationId = (): string | null => organizationId;

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly query?: Record<string, string | number | boolean | undefined | null>;
  /** Запросы вне контекста организации (логин, список организаций). */
  readonly skipOrg?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
  }

  const headers: Record<string, string> = { accept: 'application/json' };
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (accessToken) headers['authorization'] = `Bearer ${accessToken}`;
  if (!options.skipOrg && organizationId) headers['x-organization-id'] = organizationId;

  const response = await fetch(url, {
    method: options.method ?? 'GET',
    headers,
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
  });

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const error = (payload as { error?: { message?: string; code?: string } } | null)?.error;
    throw new ApiError(error?.message ?? `Ошибка ${response.status}`, response.status, error?.code ?? 'UNKNOWN');
  }
  return payload as T;
}

export const api = {
  login: (): Promise<SessionDto> =>
    request('/auth/telegram', { method: 'POST', body: { initData: getInitData() }, skipOrg: true }),

  me: (): Promise<{ organizations: Array<{ id: string; name: string; role: string }> }> =>
    request('/auth/me', { skipOrg: true }),

  createOrganization: (name: string, timezone: string): Promise<{ id: string; name: string }> =>
    request('/auth/organizations', { method: 'POST', body: { name, timezone }, skipOrg: true }),

  /* Сотрудники и доступ */
  team: (): Promise<TeamDto> => request('/team'),
  requestAccess: (organizationId: string): Promise<{ status: string }> =>
    request('/team/access-requests', { method: 'POST', body: { organizationId }, skipOrg: true }),
  decideAccess: (requestId: string, approve: boolean, role?: string): Promise<TeamDto> =>
    request(`/team/access-requests/${requestId}/decide`, {
      method: 'POST',
      body: { approve, ...(role ? { role } : {}) },
    }),
  setMemberRole: (userId: string, role: string): Promise<TeamDto> =>
    request(`/team/${userId}/role`, { method: 'PATCH', body: { role } }),
  removeMember: (userId: string): Promise<TeamDto> => request(`/team/${userId}`, { method: 'DELETE' }),

  /* Отзывы */
  feed: (params: Record<string, string | number | undefined>): Promise<ReviewFeed> =>
    request('/reviews', { query: params }),
  review: (id: string): Promise<ReviewDto> => request(`/reviews/${id}`),
  generateReply: (id: string, body: { instruction?: string; regenerate?: boolean } = {}): Promise<ReviewDto> =>
    request(`/reviews/${id}/generate`, { method: 'POST', body }),
  saveReply: (id: string, text: string): Promise<ReviewDto> =>
    request(`/reviews/${id}/reply`, { method: 'POST', body: { text } }),
  publishReply: (id: string, text?: string): Promise<ReviewDto> =>
    request(`/reviews/${id}/publish`, { method: 'POST', body: text ? { text } : {} }),
  skipReview: (id: string): Promise<ReviewDto> => request(`/reviews/${id}/skip`, { method: 'POST' }),

  /* Аналитика */
  overview: (params: Record<string, string | undefined>): Promise<AnalyticsOverview> =>
    request('/analytics/overview', { query: params }),
  checklist: (): Promise<ChecklistDto> => request('/analytics/checklist'),
  toggleChecklist: (itemKey: string, done: boolean): Promise<ChecklistDto> =>
    request('/analytics/checklist/toggle', { method: 'POST', body: { itemKey, done } }),

  /* Подключения */
  providers: (): Promise<{ providers: ProviderDescriptor[]; manualConnectorsEnabled: boolean }> =>
    request('/connect/providers'),
  connectStart: (body: {
    platform: string;
    strategy: string;
    credentials: Record<string, string>;
  }): Promise<ConnectChallengeDto> => request('/connect/start', { method: 'POST', body }),
  connect2fa: (sessionId: string, code: string): Promise<ConnectChallengeDto> =>
    request('/connect/2fa', { method: 'POST', body: { sessionId, code } }),
  connectFinish: (sessionId: string, externalOrgIds: string[]): Promise<PlatformAccountDto> =>
    request('/connect/finish', { method: 'POST', body: { sessionId, externalOrgIds } }),
  accounts: (): Promise<{ accounts: PlatformAccountDto[] }> => request('/connect/accounts'),
  syncAccount: (id: string): Promise<{ status: string }> =>
    request(`/connect/accounts/${id}/sync`, { method: 'POST' }),
  removeAccount: (id: string): Promise<{ ok: boolean }> =>
    request(`/connect/accounts/${id}`, { method: 'DELETE' }),
  saveCredentials: (id: string, credentials: Record<string, string>): Promise<{ ok: true }> =>
    request(`/connect/accounts/${id}/credentials`, { method: 'POST', body: { credentials } }),
  points: (): Promise<{ points: PointSummary[] }> => request('/connect/branches'),
  refreshBranches: (id: string): Promise<{ branchCount: number }> =>
    request(`/connect/accounts/${id}/branches/refresh`, { method: 'POST' }),
  setPointWatched: (key: string, watched: boolean): Promise<{ ok: true; watched: boolean }> =>
    request(`/connect/points/${key}`, { method: 'PATCH', body: { watched } }),

  /* Оплата */
  billing: (): Promise<BillingDto> => request('/billing'),
  updateBilling: (body: UpdateBillingDto): Promise<BillingDto> =>
    request('/billing', { method: 'PATCH', body }),

  /* Настройки */
  organization: (): Promise<Organization> => request('/settings/organization'),
  updateOrganization: (body: Partial<Organization['settings']>): Promise<Organization> =>
    request('/settings/organization', { method: 'PATCH', body }),
  rebuildBrandProfile: (): Promise<Organization> =>
    request('/settings/brand-profile/rebuild', { method: 'POST' }),
  updateBrandProfile: (text: string): Promise<Organization> =>
    request('/settings/brand-profile', { method: 'PATCH', body: { text } }),
  notifications: (): Promise<NotificationPrefsDto> => request('/settings/notifications'),
  updateNotifications: (body: Partial<NotificationPrefsDto>): Promise<NotificationPrefsDto> =>
    request('/settings/notifications', { method: 'PATCH', body }),
};

export interface ProviderField {
  readonly key: string;
  readonly label: string;
  readonly type: 'text' | 'password';
  readonly required: boolean;
}

export interface ProviderStrategy {
  readonly strategy: string;
  readonly title: string;
  /** Пояснение под названием способа — что именно произойдёт. */
  readonly hint?: string;
  readonly fields: readonly ProviderField[];
}

export interface ProviderDescriptor {
  readonly platform: string;
  readonly title: string;
  readonly strategies: readonly ProviderStrategy[];
  /** Профиль этой площадки уже подключён — повторное подключение его заменит. */
  readonly connected: { readonly displayName: string; readonly status: string } | null;
}

export interface PointPlatform {
  readonly id: string;
  readonly platform: string;
  readonly url: string | null;
  readonly rating: number | null;
  readonly reviews: number;
}

/** Точка организации со всеми площадками, на которых она присутствует. */
export interface PointSummary {
  readonly key: string;
  readonly name: string;
  readonly address: string | null;
  readonly watched: boolean;
  readonly reviews: number;
  readonly lastSyncAt: string | null;
  readonly platforms: readonly PointPlatform[];
}
